import { ALLERGEN_CONTAINS_ENUM_TO_NUMBER } from 'features/menu/components/product-detail/product-nutrition-information/types';

import { asPreviewMenuEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/as-preview-menu-entry';
import { OneMenuCatalogEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-one-menu-catalog';
import {
  AllergenContainsEnum,
  EntryTypeEnum,
  IAllergenDto,
  ProductTypeDto,
} from 'generated/graphql-gateway';
import { LANGUAGES } from 'state/intl/types';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import {
  getFirstDefaultId,
  getPickerProduct,
  isCombo,
  isItem,
  isPicker,
} from 'state/unified-menu/utils';

const PREVIEW_POS_SERVICE_MODE: PosServiceMode = 'pickup';

export function buildCatalogIndexedMenu(
  entries: readonly OneMenuCatalogEntry[],
  language: LANGUAGES
): IndexedMenu<ILocalizedMenuEntry> {
  const map = new Map<string, ILocalizedMenuEntry>();
  for (const entry of entries) {
    map.set(entry.id, asPreviewMenuEntry(entry, language));
  }
  return new IndexedMenu(map);
}

/**
 * Default combo slot items for a catalog combo (read-only preview / nutrition).
 */
export function getCatalogIncludedComboItems(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  product: ILocalizedMenuEntry
): ILocalizedMenuEntry[] {
  if (!isCombo(product) || !product.options?.entries?.length) {
    return [];
  }

  const items: ILocalizedMenuEntry[] = [];
  for (const slotRef of product.options.entries) {
    try {
      const slot = menu.get(slotRef.entryId, { type: EntryTypeEnum.COMBO_SLOT });
      const defaultItemId = getFirstDefaultId(slot);
      if (!defaultItemId) {
        continue;
      }
      const item = menu.find(defaultItemId);
      if (item) {
        items.push(item);
      }
    } catch {
      // Slot or default missing from catalog — skip.
    }
  }
  return uniqueCatalogNutritionItemsByName(items);
}

function catalogNutritionDisplayName(item: ILocalizedMenuEntry): string {
  return (item.name?.locale ?? item.id).trim();
}

/**
 * Combo nutrition/allergens: one row per display name, in catalog order.
 */
export function uniqueCatalogNutritionItemsByName(
  items: readonly ILocalizedMenuEntry[]
): ILocalizedMenuEntry[] {
  const unique: ILocalizedMenuEntry[] = [];
  const seenNames = new Set<string>();

  for (const item of items) {
    const displayName = catalogNutritionDisplayName(item).toLowerCase();
    if (displayName && seenNames.has(displayName)) {
      continue;
    }
    if (displayName) {
      seenNames.add(displayName);
    }
    unique.push(item);
  }

  return unique;
}

/**
 * Products whose nutrition represents the current catalog-preview selection.
 * Pickers resolve their default product; combos resolve each default slot item.
 */
export function getCatalogSelectedNutritionItems(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  selectedProduct: ILocalizedMenuEntry
): ILocalizedMenuEntry[] {
  if (isItem(selectedProduct)) {
    return [selectedProduct];
  }

  if (isCombo(selectedProduct)) {
    return getCatalogIncludedComboItems(menu, selectedProduct);
  }

  if (!isPicker(selectedProduct)) {
    return [];
  }

  try {
    const pickerProduct = getPickerProduct(menu, selectedProduct, {}, PREVIEW_POS_SERVICE_MODE);
    return isCombo(pickerProduct)
      ? getCatalogIncludedComboItems(menu, pickerProduct)
      : [pickerProduct];
  } catch {
    return [];
  }
}

const DRINK_PRODUCT_TYPES: ReadonlySet<ProductTypeDto> = new Set([
  ProductTypeDto.DRINKS,
  ProductTypeDto.SOFT_DRINKS,
  ProductTypeDto.SHAKES,
  ProductTypeDto.FROZEN_DRINKS,
  ProductTypeDto.JUICES,
]);

function isDrinkProductType(productType: readonly ProductTypeDto[] | null | undefined): boolean {
  if (!productType?.length) {
    return false;
  }
  return productType.some(type => DRINK_PRODUCT_TYPES.has(type));
}

/** Drink-only combo slots (same rule as PDP `getComboSlotType`). */
function isDrinkOnlyComboSlot(slot: ILocalizedMenuEntry): boolean {
  const hasDrinks = slot.productType?.includes(ProductTypeDto.DRINKS) ?? false;
  const hasSides = slot.productType?.includes(ProductTypeDto.SIDES) ?? false;
  return hasDrinks && !hasSides;
}

/**
 * Every non-drink product option across combo slots (store-less catalog preview).
 * Dedupes by id and display name; skips drink-only slots and drink items.
 */
export function getCatalogComboSlotProducts(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  product: ILocalizedMenuEntry
): ILocalizedMenuEntry[] {
  if (!isCombo(product) || !product.options?.entries?.length) {
    return [];
  }

  const items: ILocalizedMenuEntry[] = [];
  const seenIds = new Set<string>();
  const seenNames = new Set<string>();

  for (const slotRef of product.options.entries) {
    try {
      const slot = menu.get(slotRef.entryId, { type: EntryTypeEnum.COMBO_SLOT });
      if (isDrinkOnlyComboSlot(slot)) {
        continue;
      }

      for (const optionRef of slot.options?.entries ?? []) {
        if (seenIds.has(optionRef.entryId)) {
          continue;
        }
        const item = menu.find(optionRef.entryId);
        if (!item || isDrinkProductType(item.productType)) {
          continue;
        }

        const displayName = (item.name?.locale ?? item.id).trim().toLowerCase();
        if (displayName && seenNames.has(displayName)) {
          continue;
        }

        seenIds.add(optionRef.entryId);
        if (displayName) {
          seenNames.add(displayName);
        }
        items.push(item);
      }
    } catch {
      // Slot missing from catalog — skip.
    }
  }

  return items;
}

/**
 * Merge allergen flags across combo slot items, keeping the strongest contains
 * value per allergen (`_3` > `_2` > `_1` > `_0`).
 */
export function mergeCatalogItemAllergens(
  items: readonly ILocalizedMenuEntry[]
): IAllergenDto | undefined {
  const merged: Partial<Record<keyof IAllergenDto, AllergenContainsEnum>> = {};
  let hasAny = false;

  for (const item of items) {
    const allergens = item.nutrition?.allergens;
    if (!allergens) {
      continue;
    }
    hasAny = true;
    for (const [key, value] of Object.entries(allergens)) {
      if (key === '__typename' || value == null) {
        continue;
      }
      const allergenKey = key as keyof IAllergenDto;
      const next = value as AllergenContainsEnum;
      const current = merged[allergenKey];
      if (
        !current ||
        ALLERGEN_CONTAINS_ENUM_TO_NUMBER[next] > ALLERGEN_CONTAINS_ENUM_TO_NUMBER[current]
      ) {
        merged[allergenKey] = next;
      }
    }
  }

  return hasAny ? (merged as IAllergenDto) : undefined;
}
